if (!window.__alfrescoPredictiveSearchLoaded) {
window.__alfrescoPredictiveSearchLoaded = true;

const typeaheadState = {
  controller: null,
  dropdown: null,
  input: null,
  form: null,
  defaultProducts: null,
};

const defaultCollections = [
  {
    title: 'Pots & Planters',
    description: 'Garden pots, planters and vessels',
    url: '/collections/pots-planters',
  },
  {
    title: 'Outdoor Living',
    description: 'Furniture and pieces for outdoor rooms',
    url: '/collections/outdoor-living',
  },
  {
    title: 'Sculpture & Statues',
    description: 'Statement objects for gardens and entries',
    url: '/collections/sculpture-statues',
  },
  {
    title: 'Lighting',
    description: 'Outdoor lighting and lanterns',
    url: '/collections/lighting',
  },
];

const defaultSupportLinks = [
  {
    title: 'Contact us',
    description: 'Ask about availability or a specific piece',
    url: '/pages/contact',
  },
];

const money = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '';

  return new Intl.NumberFormat(document.documentElement.lang || 'en-NZ', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'NZD',
  }).format(number);
};

const imageUrl = (url, width) => {
  if (!url) return '';

  const image = new URL(url, window.location.origin);
  image.searchParams.set('width', String(width));
  return image.toString();
};

const escapeHtml = (value) =>
  String(value || '').replace(/[&<>"']/g, (character) => {
    const entities = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };

    return entities[character];
  });

const getSearchRoot = () => window.Shopify?.routes?.root || '/';

const getSearchUrl = (query) => {
  const url = new URL(`${getSearchRoot()}search`, window.location.origin);
  url.searchParams.set('type', 'product');
  url.searchParams.set('q', query);
  url.searchParams.set('options[prefix]', 'last');
  return url.toString();
};

const normalizeStorePath = (path) => new URL(path, window.location.origin).toString();

const ensureDropdown = () => {
  if (typeaheadState.dropdown) return typeaheadState.dropdown;

  const dropdown = document.createElement('div');
  dropdown.className = 'alfresco-predictive-search';
  dropdown.hidden = true;
  dropdown.setAttribute('role', 'listbox');
  document.body.append(dropdown);
  typeaheadState.dropdown = dropdown;

  return dropdown;
};

const positionDropdown = () => {
  const { input, dropdown } = typeaheadState;
  if (!input || !dropdown || dropdown.hidden) return;

  const rect = input.getBoundingClientRect();
  dropdown.style.setProperty('--search-left', '0px');
  dropdown.style.setProperty('--search-top', `${rect.bottom + 10}px`);
  dropdown.style.setProperty('--search-width', '100vw');
};

const closeDropdown = () => {
  if (typeaheadState.dropdown) {
    typeaheadState.dropdown.hidden = true;
    typeaheadState.dropdown.innerHTML = '';
  }
};

const isSearchInput = (input) =>
  input instanceof HTMLInputElement &&
  input.name === 'q' &&
  (input.type === 'search' || input.matches('.search-input, .search-page-input'));

const prepareInput = (input) => {
  if (!isSearchInput(input)) return;

  input.setAttribute('autocomplete', 'off');
  input.setAttribute('autocorrect', 'off');
  input.setAttribute('autocapitalize', 'off');
  input.setAttribute('spellcheck', 'false');
};

const renderProductList = (products, emptyText) => {
  if (products.length === 0) {
    return `<div class="alfresco-predictive-search__empty">${escapeHtml(emptyText)}</div>`;
  }

  return `
    <ul class="alfresco-predictive-search__product-list">
      ${products
        .map((product) => {
          const image = product.featured_image?.url || product.image || '';
          const price = money(product.price);
          const vendor = product.vendor || product.type || '';

          return `
            <li class="alfresco-predictive-search__item" role="option">
              <a class="alfresco-predictive-search__product-link" href="${escapeHtml(product.url)}">
                <span class="alfresco-predictive-search__media">
                  ${
                    image
                      ? `<img src="${escapeHtml(imageUrl(image, 176))}" alt="${escapeHtml(product.featured_image?.alt || product.title)}" loading="lazy">`
                      : ''
                  }
                </span>
                <span class="alfresco-predictive-search__body">
                  ${vendor ? `<span class="alfresco-predictive-search__eyebrow">${escapeHtml(vendor)}</span>` : ''}
                  <span class="alfresco-predictive-search__title">${escapeHtml(product.title)}</span>
                  ${price ? `<span class="alfresco-predictive-search__price">${escapeHtml(price)}</span>` : ''}
                </span>
              </a>
            </li>
          `;
        })
        .join('')}
    </ul>
  `;
};

const renderLinkList = (items, emptyText) => {
  if (items.length === 0) {
    return `<div class="alfresco-predictive-search__empty">${escapeHtml(emptyText)}</div>`;
  }

  return `
    <ul class="alfresco-predictive-search__link-list">
      ${items
        .map(
          (item) => `
            <li>
              <a class="alfresco-predictive-search__text-link" href="${escapeHtml(normalizeStorePath(item.url))}">
                <span class="alfresco-predictive-search__title">${escapeHtml(item.title)}</span>
                ${
                  item.description
                    ? `<span class="alfresco-predictive-search__description">${escapeHtml(item.description)}</span>`
                    : ''
                }
              </a>
            </li>
          `
        )
        .join('')}
    </ul>
  `;
};

const renderDropdown = (query, results, mode = 'results') => {
  const dropdown = ensureDropdown();
  const products = (results.products || []).slice(0, 6);
  const collections = (results.collections || []).slice(0, 4);
  const isDefault = mode === 'default';

  dropdown.innerHTML = `
    <div class="alfresco-predictive-search__panel">
      <section class="alfresco-predictive-search__section alfresco-predictive-search__section--products">
        <div class="alfresco-predictive-search__heading">${isDefault ? 'Suggested products' : 'Products'}</div>
        ${renderProductList(products, isDefault ? 'Start typing to search products.' : `No products found for "${query}".`)}
        ${
          isDefault
            ? ''
            : `<a class="alfresco-predictive-search__view-all" href="${escapeHtml(getSearchUrl(query))}">View all for "${escapeHtml(query)}"</a>`
        }
      </section>
      <aside class="alfresco-predictive-search__aside">
        <section class="alfresco-predictive-search__section">
          <div class="alfresco-predictive-search__heading">${isDefault ? 'Collections' : 'Matching collections'}</div>
          ${renderLinkList(isDefault ? defaultCollections : collections, 'No matching collections.')}
        </section>
        ${
          isDefault
            ? `<section class="alfresco-predictive-search__section">
                <div class="alfresco-predictive-search__heading">Support</div>
                ${renderLinkList(defaultSupportLinks, '')}
              </section>`
            : ''
        }
      </div>
    </div>
  `;

  dropdown.hidden = false;
  positionDropdown();
};

const fetchSuggestions = async (query) => {
  if (typeaheadState.controller) {
    typeaheadState.controller.abort();
  }

  typeaheadState.controller = new AbortController();

  const url = new URL(`${getSearchRoot()}search/suggest.json`, window.location.origin);
  url.searchParams.set('q', query);
  url.searchParams.set('resources[type]', 'product,collection');
  url.searchParams.set('resources[limit]', '6');
  url.searchParams.set('resources[limit_scope]', 'each');
  url.searchParams.set('resources[options][unavailable_products]', 'last');
  url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,variants.sku,vendor,tag');

  const response = await fetch(url.toString(), {
    signal: typeaheadState.controller.signal,
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Predictive search failed: ${response.status}`);
  }

  const data = await response.json();
  return {
    products: data?.resources?.results?.products || [],
    collections: data?.resources?.results?.collections || [],
  };
};

const showDefaultSuggestions = async (input) => {
  typeaheadState.input = input;
  typeaheadState.form = input.closest('form');

  if (typeaheadState.defaultProducts) {
    renderDropdown('', { products: typeaheadState.defaultProducts, collections: [] }, 'default');
    return;
  }

  try {
    const results = await fetchSuggestions('pot');
    typeaheadState.defaultProducts = results.products;
    renderDropdown('', { products: typeaheadState.defaultProducts, collections: [] }, 'default');
  } catch (error) {
    if (error.name !== 'AbortError') {
      renderDropdown('', { products: [], collections: [] }, 'default');
    }
  }
};

let debounceTimer = 0;

const onInput = (event) => {
  const input = event.target;
  if (!isSearchInput(input)) return;

  prepareInput(input);
  typeaheadState.input = input;
  typeaheadState.form = input.closest('form');

  window.clearTimeout(debounceTimer);

  const query = input.value.trim();
  if (query.length < 1) {
    showDefaultSuggestions(input);
    return;
  }

  debounceTimer = window.setTimeout(async () => {
    try {
      const results = await fetchSuggestions(query);
      renderDropdown(query, results);
    } catch (error) {
      if (error.name !== 'AbortError') {
        closeDropdown();
      }
    }
  }, 180);
};

const onFocus = (event) => {
  const input = event.target;
  if (!isSearchInput(input)) return;

  prepareInput(input);

  if (input.value.trim().length < 1) {
    showDefaultSuggestions(input);
  }
};

const onSearchTriggerClick = (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('[data-alfresco-search-trigger]') : null;
  if (!trigger) return;

  event.preventDefault();

  const input = document.querySelector('.alfresco-nook-header__search input[name="q"], input.search-input[name="q"], input[name="q"]');
  if (!(input instanceof HTMLInputElement)) return;

  input.focus();

  if (input.value.trim().length < 1) {
    showDefaultSuggestions(input);
  }
};

const onKeydown = (event) => {
  if (!isSearchInput(event.target)) return;

  if (event.key === 'Escape') {
    closeDropdown();
  }
};

const prepareSearchInputs = () => {
  document.querySelectorAll('input[name="q"]').forEach(prepareInput);
};

prepareSearchInputs();
document.addEventListener('DOMContentLoaded', prepareSearchInputs);
window.addEventListener('pageshow', prepareSearchInputs);
document.addEventListener('input', onInput, true);
document.addEventListener('focusin', onFocus);
document.addEventListener('keydown', onKeydown);
document.addEventListener('click', onSearchTriggerClick);
document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  if (target.closest('.alfresco-predictive-search') || target.closest('input[name="q"]')) return;
  closeDropdown();
});
window.addEventListener('resize', positionDropdown);
window.addEventListener('scroll', positionDropdown, true);
}
